/**
 * Применение темы к DOM: режим (light/dark класс), инъекция CSS-токенов
 * воркспейса и подгрузка Google-шрифтов.
 *
 * Источник правды по значениям — generate.ts; здесь только сайд-эффекты над
 * `document`. Все функции безопасны при `typeof document === 'undefined'`.
 */
import {buildThemeCss} from './generate';
import {findGoogleQuery} from './palettes';
import type {ThemeMode, WorkspaceTheme} from './types';

/** localStorage-ключ личного режима оформления. */
export const THEME_MODE_STORAGE_KEY = 'colver-rop:theme-mode';

const WORKSPACE_STYLE_ID = 'colver-workspace-theme';
const FONT_LINK_PREFIX = 'colver-font-';

const hasDom = (): boolean => typeof document !== 'undefined';

/** Системное предпочтение тёмной темы. */
export const systemPrefersDark = (): boolean =>
  hasDom() &&
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

/** Итоговая «темнота» для режима с учётом system. */
export const resolveDark = (mode: ThemeMode): boolean =>
  mode === 'dark' || (mode === 'system' && systemPrefersDark());

/** Переключить класс `.dark` на <html> по режиму. */
export const applyThemeMode = (mode: ThemeMode): void => {
  if (!hasDom()) return;
  document.documentElement.classList.toggle('dark', resolveDark(mode));
};

/** Прочитать сохранённый режим (для раннего bootstrap в main.tsx). */
export const readStoredMode = (): ThemeMode => {
  if (typeof window === 'undefined') return 'system';
  try {
    const raw = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // ignore
  }
  return 'system';
};

/**
 * Ранняя инициализация режима (вызывается в main.tsx до рендера) — чтобы не
 * мигало светлой темой на тёмном выборе.
 */
export const bootstrapThemeMode = (): void => {
  applyThemeMode(readStoredMode());
};

/** Подгрузить Google-шрифт (idempotent) по сохранённому CSS-family. */
const loadFont = (family: string | null | undefined): void => {
  if (!hasDom() || !family) return;
  const query = findGoogleQuery(family);
  if (!query) return;
  const id = FONT_LINK_PREFIX + query;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${query}&display=swap`;
  document.head.appendChild(link);
};

/**
 * Применить (или снять) тему воркспейса. `null` — убрать инъекцию и вернуться к
 * базовым токенам index.css.
 */
export const applyWorkspaceTheme = (
  theme: WorkspaceTheme | null | undefined,
): void => {
  if (!hasDom()) return;
  const existing = document.getElementById(WORKSPACE_STYLE_ID);
  if (!theme) {
    existing?.remove();
    return;
  }
  loadFont(theme.fontHeading);
  loadFont(theme.fontBody);
  loadFont(theme.fontMono);
  const style =
    (existing as HTMLStyleElement | null) ?? document.createElement('style');
  if (!existing) {
    style.id = WORKSPACE_STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = buildThemeCss(theme);
};
